import { CSSProperties, memo, ReactNode, useCallback } from 'react'
import classNames from 'classnames'
import { IconSpinner } from 'components/IconSpinner'
import { KeyCodeEnum } from 'enums/keyCodes'
import { useHotKey } from 'hooks/useHotKey'
import { stopBubbling } from 'utils/event'

import { DialogDesktop } from '../DialogDesktop'

import s from './ModalDesktop.module.less'

export type TSize = 'sm' | 'nm' | 'lg' | 'xl' | 'xxl'

interface IProps {
  children: ReactNode
  className?: string
  /** Слот для нижнего блока диалога */
  footer?: JSX.Element
  formName?: string
  /** Слот для верхнего блока диалога */
  header?: JSX.Element
  id?: string
  /** Отображение кнопки закрытия окна */
  isCloseButton?: boolean
  /** Закрытие окна по нажатию на Esc */
  isCloseOnEsc?: boolean
  /** Закрытие окна по клику вне области этого окна */
  isCloseOnOutsideClick?: boolean
  /** Отображение окна как диалогового (прижатого к низу) */
  isDialogType?: boolean
  /** Отображение загрузки данных окна */
  isLoading?: boolean
  /** Отображение горизонтальных отступов контентной части */
  isOffsets?: boolean
  isOrder?: boolean
  isOrderCreate?: boolean
  noPadding?: boolean
  onClose?: () => void
  onSubmit?: () => void
  /** Размер окна */
  size?: TSize
  style?: CSSProperties
}

export const ModalDesktop = memo<IProps>(function ModalDesktop({
  children,
  className,
  footer,
  formName,
  header,
  id,
  isDialogType = false,
  isLoading = false,
  isCloseButton = true,
  isCloseOnEsc = true,
  isCloseOnOutsideClick = true,
  isOffsets = true,
  onClose,
  onSubmit,
  size = 'nm',
  style,
  isOrder,
  noPadding,
  isOrderCreate
}) {
  const handlePressOnEsc = useCallback(() => {
    if (isCloseOnEsc && onClose) {
      onClose()
    }
  }, [isCloseOnEsc, onClose])

  const content = () => {
    if (isLoading) {
      return <IconSpinner size="nm" />
    }

    return (
      <div
        className={classNames(
          s.container,
          isOrder ? s.orderContainer : '',
          isOrderCreate ? s.orderCreateContainer : ''
        )}
      >
        <DialogDesktop
          footer={footer}
          header={header}
          id={id}
          isCloseButton={isCloseButton}
          isCloseOnOutsideClick={isCloseOnOutsideClick}
          isOffsets={isOffsets}
          isOrder={isOrder}
          noPadding={noPadding}
          onClose={onClose}
          size={size}
        >
          {children}
        </DialogDesktop>
      </div>
    )
  }

  const isForm = !!onSubmit
  const props = {
    className: classNames(s.ModalDesktop, className, {
      [s['ModalDesktop_dialog-type']]: isDialogType,
      [s.ModalDesktop_loading]: isLoading
    }),
    id,
    onClick: stopBubbling,
    style
  }

  useHotKey(KeyCodeEnum.ESC, handlePressOnEsc)

  return isForm ? (
    <form {...props} name={formName} onSubmit={onSubmit}>
      {content()}
    </form>
  ) : (
    <div {...props}>{content()}</div>
  )
})
